/* global G */
import { curry, setKey } from 'lib/util'
import PlatformEvent from 'lib/util/event'
import { actionFn, actionWithHooks } from 'lib/sequence/module/action'
import refresh from 'lib/sequence/model/api/refresh'
import resetModel from 'lib/sequence/model/api/reset'
import { reset } from 'lib/sequence/component/state/value'
import { detail } from 'app/_shared/events'
import { expose } from 'app/_shared/events/device/find'
import find from 'lib/sequence/component/children/find'
import setSerialFieldState from 'app/_shared/events/device/setSerialFieldState'
import { get } from 'lib/sequence/component/state/value'

/**
 * Returns the current action's UNDO state (dirty flag) value. If value is passed, it sets it as the
 * value of the flag.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @param {boolean} [value]             value to set for the current action's UNDO state
 * @returns {boolean}
 */
const undo = (module, value) => {
  if (value) {
    setKey(value, G.UNDO, module[G.STATE][G.ACTION][G.STATE])
    return value
  }
  return module[G.STATE][G.ACTION][G.STATE][G.UNDO]
}

/**
 * Shows a dialog informing the user that a device was found.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {string} organisation         the name of the organisation the found device is
 *                                      installed at
 * @param {function} okHandler          executed if the user clicks 'ok'
 * @param {function} cancelHandler      executed if the user clicks 'cancel'
 */
const showDeviceFoundDialog = async (module, organisation, okHandler, cancelHandler) => {
  const eventBus = module[G.ADAPTER][G.EVENTS]

  eventBus.dispatch(eventBus.type(G.DATA, G.UNDO), {
    title: await module[G.ADAPTER][G.INTL]._t(
      'dialog.deviceFound.title',
      {
        ns: 'device',
        _key: 'dialog.deviceFound.title',
        defaultValue: 'Device already assigned',
      },
    ),
    text: await module[G.ADAPTER][G.INTL]._t(
      'dialog.deviceFound.text',
      {
        ns: 'device',
        _key: 'dialog.deviceFound.text',
        defaultValue: 'Device is already assigned to "{{organisation}}". Please, make sure you'
          + ' want to re-assign it to another customer.\n\nIn case the device has only'
          + ' changed the location, not the ownership, press Ok to open the device'
          + ' and change the installation location.',
        organisation,
      },
    ),
    children: {
      cancel: {
        key: 'cancel',
        value: await module[G.ADAPTER][G.INTL]._t(
          'button.cancel',
          {
            ns: 'common',
            _key: 'button.cancel',
            defaultValue: 'cancel',
          },
        ),
      },
      ok: {
        key: 'ok',
        value: await module[G.ADAPTER][G.INTL]._t(
          'button.ok',
          {
            ns: 'common',
            _key: 'button.ok',
            defaultValue: 'ok',
          },
        ),
      },
    },
    okHandler,
    cancelHandler,
  })
}

/**
 * Attempts to obtain the product with key `event.detail.key` and sets it as the product
 * property of the current model.
 *
 * @param {Gaia.AppModule.Spec} module    the current module composition object
 * @param {Gaia.Component.Spec} component the current action's main component
 * @param {Event|Object} eventOrItem      the event object that triggered this handler
 * @param {Object} eventOrItem.detail     custom event details
 * @returns {Promise<void>} void
 */
// eslint-disable-next-line no-unused-vars
const selectItem = async (module, component, eventOrItem) => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const model = module[G.MODEL]
  const { item: selectedItem = eventOrItem } = eventOrItem?.detail || {}
  const item = expose(selectedItem) || {}
  const { invalid, serviceItem, installedAt } = item
  const serviceItemExists = serviceItem && serviceItem._rev?.charAt(0) !== '0'

  const components = find(actionComponent)
  const { serial: serialField, numberCommission: numberComissionField, resultsModal } = components

  const serial = get(serialField)
  // clearing currently stored data
  resetModel(model)

  if (serviceItemExists) {
    const organisation = installedAt?.value.name
    await showDeviceFoundDialog(module, organisation, async () => {
      await detail(module, component, new PlatformEvent('click', serviceItem))
    }, async () => {
      resetModel(model)
      setSerialFieldState(module, serialField, ['', item])
      reset(serialField)
      reset(numberComissionField)

      await refresh(model, { serial: null, numberCommission: null })

      // await actionFn(module[G.STATE][G.ACTION])()
      await module[G.ADAPTER][G.UI].update(module)
    })
  } else if (serviceItem) {
    // updating model with new data
    await refresh(model, serviceItem)
  } else if (!invalid) {
    // clearing currently stored data
    resetModel(model)
    await refresh(model, { serial, name: '' })
  }

  setSerialFieldState(module, serialField, [serial, item])

  const newSerial = !model[G.CHILDREN].serial[G.CACHE] && serial
  undo(module, newSerial ? true : undo(module))

  setKey(false, 'open', resultsModal[G.STATE])

  await actionFn(module[G.STATE][G.ACTION])()
  await module[G.ADAPTER][G.UI].update(module)
}

export default curry(selectItem)
